/**
 * Inventory Overdue PO Exception Config
 *
 * Covers: ex-inv-ls-overdue-po
 *
 * Open purchase orders past their expected delivery date.
 */
import { formatCurrency, formatDate } from '../../../utils/inspectorFormatters'
import { buildIssuesColumn } from '../shared/issuesColumn'
import { PLATFORM_CONTEXT_COLUMNS } from '../shared/platformColumns'
import {
  composeRowSubtitle,
  datePart,
  moneyPart,
  textPart,
} from '../shared/rowSubtitle'
import {
  consignmentReferenceSortValue,
  createConsignmentLinkCell,
} from './shared/consignmentRenderers'
import { createDaysValueRenderer } from './shared/daysRenderers'
import type { ExceptionConfig, ExceptionColumnDef } from '../types'

const REFERENCE_FROZEN_WIDTH = 220

const renderDaysOverdue = createDaysValueRenderer({ redAtDays: 14, orangeAtDays: 7 })

const columns: ExceptionColumnDef[] = [
  ...PLATFORM_CONTEXT_COLUMNS,
  buildIssuesColumn(),
  {
    key: 'name',
    label: 'Reference',
    width: REFERENCE_FROZEN_WIDTH,
    minWidth: REFERENCE_FROZEN_WIDTH,
    freeze: 'left',
    render: createConsignmentLinkCell('PO'),
    link: { field: 'lightspeed_link', system: 'lightspeed' },
    getSortValue: consignmentReferenceSortValue,
  },
  {
    key: 'due_at_local',
    label: 'Due Date',
    render: (value, _row, ctx) => ctx.variant === 'inspector' && value ? formatDate(value) : (value || '-'),
    exportFormat: 'date',
  },
  {
    key: 'supplier_name',
    label: 'Supplier',
    render: (value) => value || '-',
  },
  {
    key: 'outlet_name',
    label: 'Outlet',
    render: (value) => value || '-',
  },
  {
    key: 'days_value',
    label: 'Days Overdue',
    align: 'right',
    render: renderDaysOverdue,
    getSortValue: (row) => Number(row.days_value) || 0,
  },
  {
    key: 'total_cost',
    label: 'Total Cost',
    align: 'right',
    render: (value, _row, ctx) => ctx.variant === 'inspector' ? formatCurrency(value) : undefined,
    exportFormat: 'currency',
  },
]

export const overduePoConfig: ExceptionConfig = {
  codes: ['ex-inv-ls-overdue-po'],
  columns,
  defaultSortColumn: 'due_at_local',
  defaultSortDirection: 'desc',
  complexity: 'simple',
  rowKeyField: 'ls_id',
  getRowTitle: (row) =>
    row.reference_number
      ? `PO ${row.reference_number}`
      : `PO ${String(row.ls_id ?? '').slice(0, 8)}`,
  getRowSubtitle: (row) =>
    composeRowSubtitle([
      textPart(row.supplier_name),
      row.due_at_local ? `due ${datePart(row.due_at_local)}` : null,
      typeof row.days_value === 'number' && row.days_value > 0
        ? `${row.days_value} days overdue`
        : null,
      moneyPart(row.total_cost),
    ]),
}
